"use client";

import {
  abortMultipartUpload,
  completeMultipartUpload,
  refreshMultipartUploadPart,
  requestUploadTarget,
} from "@/actions/image";
import { moderateImageFile } from "@/components/image-upload/image-moderation";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { loadImage, loadVideo, optimizeImage } from "@/lib/image-utils";
import { createClientLogger } from "@/lib/logging/client";
import { createRandomId, createShortRandomId } from "@/lib/random-id";
import { encodeThumbHashFromImage } from "./blurhash";
import type { MediaAssetSlim, MediaUploadScopeDomain } from "./types";

/**
 * Headless client-side media pipeline: encode → moderate → upload, with no
 * React involved. `ImageUpload` composes these for its grid/feed UI; the
 * Stories composer calls `processStoryMedia` directly for single files.
 */

import type { RequestUploadTargetSuccess } from "@/actions/image";
import { mediaUrlFromKey } from "./paths";
import { waitForProcessedMedia } from "./processing-client";

const logger = createClientLogger("lib/media/upload-pipeline");

export type MediaUploadScope = {
  domain: MediaUploadScopeDomain;
  slot: string;
  entityRef?: string;
};

type ImageEncodeWorkerSuccess = {
  requestId: string;
  ok: true;
  encodedBuffer: ArrayBuffer;
  mimeType: string;
  width: number;
  height: number;
  fileName: string;
};

type ImageEncodeWorkerFailure = {
  requestId: string;
  ok: false;
  error: string;
};

type ImageEncodeWorkerResponse =
  | ImageEncodeWorkerSuccess
  | ImageEncodeWorkerFailure;

export type EncodeImageInWorkerOptions = {
  allowJpegFallback?: boolean;
  requireModernFormat?: boolean;
  outputBaseName?: string;
  skipOptimization?: boolean;
  maxDimension?: number;
  preferWebp?: boolean;
};

export type EncodedImageResult = {
  file: File;
  width: number;
  height: number;
  mimeType: string;
};

export type UploadedBinary = {
  assetId: string;
  key: string;
  url: string;
  asset?: MediaAssetSlim;
};

const AVIF_MAX_DIMENSION = 3072;
const MULTIPART_UPLOAD_MAX_ATTEMPTS = 3;

const RETRYABLE_UPLOAD_STATUS_CODES = new Set([
  408, 409, 425, 429, 500, 502, 503, 504,
]);

const normalizeMultipartEtag = (value: string): string => {
  const trimmed = value.trim();
  const unquoted = trimmed.replace(/^"+|"+$/g, "");
  if (!unquoted) {
    return trimmed;
  }

  // Ensure ETag values are consistently quoted for multipart completion.
  return `"${unquoted}"`;
};

const canUseImageEncodeWorker = () =>
  typeof Worker !== "undefined" &&
  typeof OffscreenCanvas !== "undefined" &&
  typeof createImageBitmap === "function" &&
  typeof OffscreenCanvas.prototype.convertToBlob === "function";

const waitForRetry = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

/**
 * Re-encode an image to AVIF/WebP (JPEG fallback unless disabled) inside a
 * dedicated worker so the main thread stays responsive. Falls back to the
 * optimized source file when the worker path is unavailable.
 */
export const encodeImageInWorker = async (
  file: File,
  options?: EncodeImageInWorkerOptions,
): Promise<EncodedImageResult> => {
  // Canvas encoders flatten animation. Let Sharp retain frames and resize
  // animated images on the worker instead of silently turning them into stills.
  const header = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  const animatedGif = file.type === "image/gif" || /\.gif$/i.test(file.name);
  const animatedWebp =
    file.type === "image/webp" &&
    String.fromCharCode(...header.slice(12, 16)) === "VP8X" &&
    Boolean(header[20] & 2);
  if ((animatedGif || animatedWebp) && !options?.requireModernFormat) {
    return {
      file,
      width: 0,
      height: 0,
      mimeType: animatedGif ? "image/gif" : "image/webp",
    };
  }
  const maxDimension = options?.maxDimension ?? AVIF_MAX_DIMENSION;
  const allowJpegFallback = options?.allowJpegFallback ?? true;
  const requireModernFormat = options?.requireModernFormat ?? false;
  const outputBaseName =
    options?.outputBaseName ?? file.name.replace(/\.[^/.]+$/, "");

  // 0.95 was wasteful for an INTERMEDIATE. This step exists to bound
  // dimensions before the real encode, and its JPEG output is re-encoded
  // immediately afterwards — so the only thing the extra quality bought was a
  // bigger buffer to hand the worker. It stopped being harmless when the
  // worker was unavailable: then this intermediate IS the upload, at a size
  // nothing downstream would have chosen.
  const optimized = options?.skipOptimization
    ? file
    : await optimizeImage(file, {
        maxWidth: maxDimension,
        maxHeight: maxDimension,
        quality: 0.9,
      });

  if (!canUseImageEncodeWorker()) {
    const mimeType = optimized.type || "image/jpeg";
    const isModernFormat =
      mimeType === "image/avif" || mimeType === "image/webp";

    if (requireModernFormat && !isModernFormat) {
      throw new Error("This device/browser cannot encode AVIF or WebP images.");
    }

    return {
      file: optimized,
      width: 0,
      height: 0,
      mimeType,
    };
  }

  let worker: Worker | null = null;

  try {
    worker = new Worker(
      new URL("./media-image-encoder.worker.ts", import.meta.url),
    );

    const requestId = createRandomId();
    const arrayBuffer = await optimized.arrayBuffer();

    const response = await new Promise<ImageEncodeWorkerResponse>(
      (resolve, reject) => {
        if (!worker) {
          reject(new Error("Image worker unavailable."));
          return;
        }

        const timeout = window.setTimeout(() => {
          reject(new Error("Image encoding timed out."));
        }, 45_000);

        worker.onmessage = (event: MessageEvent<ImageEncodeWorkerResponse>) => {
          const message = event.data;
          if (message.requestId !== requestId) {
            return;
          }

          window.clearTimeout(timeout);
          resolve(message);
        };

        worker.onerror = (event) => {
          window.clearTimeout(timeout);
          reject(event.error ?? new Error("Image encoding failed."));
        };

        worker.postMessage({
          requestId,
          arrayBuffer,
          mimeType: optimized.type || "image/jpeg",
          fileName: optimized.name,
          maxWidth: maxDimension,
          maxHeight: maxDimension,
          quality: 0.92,
          allowJpegFallback,
          preferWebp: options?.preferWebp,
        });
      },
    );

    if (!response.ok) {
      throw new Error(response.error);
    }

    const isModernFormat =
      response.mimeType === "image/avif" || response.mimeType === "image/webp";
    if (requireModernFormat && !isModernFormat) {
      throw new Error(
        "Poster encoding must be AVIF or WebP; JPEG fallback is disabled.",
      );
    }

    const extension = response.mimeType.includes("avif")
      ? "avif"
      : response.mimeType.includes("webp")
        ? "webp"
        : "jpg";

    const encodedFile = new File(
      [response.encodedBuffer],
      `${outputBaseName}.${extension}`,
      {
        type: response.mimeType,
        lastModified: Date.now(),
      },
    );

    return {
      file: encodedFile,
      width: response.width,
      height: response.height,
      mimeType: response.mimeType,
    };
  } catch (error) {
    if (requireModernFormat) {
      throw error;
    }

    logger.error(
      "Image AVIF/WebP encoding failed, falling back to JPEG",
      error,
    );
    return {
      file: optimized,
      width: 0,
      height: 0,
      mimeType: optimized.type || "image/jpeg",
    };
  } finally {
    worker?.terminate();
  }
};

/**
 * Grab a representative frame from a video (via mediabunny) and encode it as
 * an image suitable for use as the upload's poster.
 */
export const createVideoPoster = async (file: File): Promise<File> => {
  const { Input, BlobSource, ALL_FORMATS, CanvasSink } = await import(
    "mediabunny"
  );

  let input: InstanceType<typeof Input> | null = null;
  try {
    input = new Input({
      source: new BlobSource(file),
      formats: ALL_FORMATS,
    });

    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      throw new Error("No video track found for poster generation.");
    }

    const duration = await input.computeDuration();
    const targetTimeSec = Number.isFinite(duration)
      ? Math.min(1, Math.max(0, duration * 0.25))
      : 0;

    const sink = new CanvasSink(videoTrack);
    const wrappedCanvas = await sink.getCanvas(targetTimeSec);
    if (!wrappedCanvas) {
      throw new Error("Failed to render video poster frame.");
    }

    const canvas = wrappedCanvas.canvas;
    const posterBlob = await new Promise<Blob | null>((resolve) => {
      if (canvas instanceof HTMLCanvasElement) {
        canvas.toBlob((blob) => resolve(blob), "image/png", 1);
      } else {
        canvas
          .convertToBlob({ type: "image/png", quality: 1 })
          .then(resolve)
          .catch(() => resolve(null));
      }
    });

    if (!posterBlob) {
      throw new Error("Failed to render video poster frame.");
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const posterFrameFile = new File([posterBlob], `${baseName}-poster.png`, {
      type: "image/png",
      lastModified: Date.now(),
    });

    const encodedPoster = await encodeImageInWorker(posterFrameFile, {
      allowJpegFallback: true,
      outputBaseName: `${baseName}-poster`,
    });

    return encodedPoster.file;
  } finally {
    if (input) {
      input.dispose();
    }
  }
};

/**
 * Upload a binary to R2 via a signed target — single PUT for small files,
 * multipart (with per-part retry + URL refresh + abort on failure) for large
 * ones. The server decides the mode; thresholds live in lib/media/r2.
 */
export const uploadBinary = async (input: {
  file: File;
  mimeType: string;
  scope: MediaUploadScope;
  draftId: string;
  /** Byte progress for multipart uploads (single PUT reports 0 then total). */
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  onUploaded?: (asset: MediaAssetSlim) => void;
  signal?: AbortSignal;
  target?: RequestUploadTargetSuccess;
}): Promise<UploadedBinary> => {
  // Uploads are the single most failure-prone thing the app does on a phone:
  // flaky mobile data, backgrounded WebViews, huge camera files. Without a
  // start/succeed/fail triple there is no upload success rate to watch, and a
  // regression only shows up as users quietly not posting.
  const startedAt = Date.now();
  const uploadProperties = {
    domain: input.scope.domain,
    slot: input.scope.slot,
    mime_type: input.mimeType,
    size_bytes: input.file.size,
  };

  captureClientEvent(ANALYTICS_EVENTS.MEDIA_UPLOAD_STARTED, uploadProperties);

  try {
    if (input.mimeType.startsWith("video/") && !input.target) {
      const { optimizeVideoForUpload } = await import("./client-video");
      const file = await optimizeVideoForUpload(input.file, input.signal);
      input = { ...input, file, mimeType: file.type || input.mimeType };
    }
    input.signal?.throwIfAborted();
    let uploaded = await uploadBinaryInner(input);
    if (/^(image|video)\//.test(input.mimeType)) {
      input.onUploaded?.({ id: uploaded.assetId, key: uploaded.key, order: 0 });
      const asset = await waitForProcessedMedia(uploaded.key, {
        signal: input.signal,
      });
      uploaded = {
        ...uploaded,
        key: asset.key,
        url: mediaUrlFromKey(asset.key),
        asset,
      };
    }
    captureClientEvent(ANALYTICS_EVENTS.MEDIA_UPLOAD_SUCCEEDED, {
      ...uploadProperties,
      duration_ms: Date.now() - startedAt,
    });
    return uploaded;
  } catch (error) {
    captureClientEvent(ANALYTICS_EVENTS.MEDIA_UPLOAD_FAILED, {
      ...uploadProperties,
      duration_ms: Date.now() - startedAt,
      error_message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const uploadBinaryInner = async (input: {
  file: File;
  mimeType: string;
  scope: MediaUploadScope;
  draftId: string;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  onUploaded?: (asset: MediaAssetSlim) => void;
  signal?: AbortSignal;
  target?: RequestUploadTargetSuccess;
}): Promise<UploadedBinary> => {
  const target =
    input.target ??
    (await requestUploadTarget({
      domain: input.scope.domain,
      slot: input.scope.slot,
      entityRef: input.scope.entityRef,
      draftId: input.draftId,
      fileName: input.file.name,
      mimeType: input.mimeType,
      bytes: input.file.size,
    }));

  if ("error" in target) {
    throw new Error(target.error);
  }

  if (target.mode === "single") {
    if (!target.uploadUrl) {
      throw new Error("Upload URL missing for single-part upload.");
    }

    const requestHeaders = new Headers(target.headers);
    if (!requestHeaders.has("content-type")) {
      requestHeaders.set("content-type", input.mimeType);
    }

    // fetch exposes no upload progress events: report start, then completion.
    input.onProgress?.(0, input.file.size);
    const response = await fetch(target.uploadUrl, {
      method: "PUT",
      headers: requestHeaders,
      body: input.file,
      signal: input.signal,
    });

    if (!response.ok) {
      throw new Error(`Upload failed (${response.status})`);
    }
    input.onProgress?.(input.file.size, input.file.size);

    return {
      assetId: target.assetId,
      key: target.key,
      url: target.url,
    };
  }

  if (!target.multipart) {
    throw new Error("Multipart upload metadata is missing.");
  }

  const multipart = target.multipart;
  const uploadedParts: Array<{ partNumber: number; etag: string }> = [];

  const uploadMultipartPart = async (inputPart: {
    partNumber: number;
    uploadUrl: string;
    chunk: Blob;
  }) => {
    let currentUploadUrl = inputPart.uploadUrl;

    for (let attempt = 0; attempt < MULTIPART_UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(currentUploadUrl, {
          method: "PUT",
          body: inputPart.chunk,
          signal: input.signal,
        });

        if (response.ok) {
          const rawEtag =
            response.headers.get("etag") ?? response.headers.get("ETag");
          if (!rawEtag) {
            throw new Error(`Missing ETag for part ${inputPart.partNumber}`);
          }

          return normalizeMultipartEtag(rawEtag);
        }

        const canRefreshUrl =
          response.status === 401 || response.status === 403;
        if (canRefreshUrl && attempt < MULTIPART_UPLOAD_MAX_ATTEMPTS - 1) {
          const refreshed = await refreshMultipartUploadPart({
            key: target.key,
            uploadId: multipart.uploadId,
            partNumber: inputPart.partNumber,
          });

          if ("error" in refreshed) {
            throw new Error(refreshed.error);
          }

          currentUploadUrl = refreshed.uploadUrl;
          continue;
        }

        if (
          RETRYABLE_UPLOAD_STATUS_CODES.has(response.status) &&
          attempt < MULTIPART_UPLOAD_MAX_ATTEMPTS - 1
        ) {
          await waitForRetry(500 * (attempt + 1));
          continue;
        }

        throw new Error(
          `Multipart upload failed at part ${inputPart.partNumber} (${response.status})`,
        );
      } catch (error) {
        if (attempt >= MULTIPART_UPLOAD_MAX_ATTEMPTS - 1) {
          throw error;
        }

        await waitForRetry(500 * (attempt + 1));
      }
    }

    throw new Error(`Multipart upload failed at part ${inputPart.partNumber}`);
  };

  try {
    // Parts upload in parallel (4-way): a 60s story is several parts, and
    // sequential PUTs on a mobile radio multiply wall time for no reason.
    const MULTIPART_CONCURRENCY = 4;
    const partSize = multipart.partSize;
    const queue = [...target.multipart.parts];
    let completedBytes = 0;
    let partFailure: unknown;
    const workers = new Array(Math.min(MULTIPART_CONCURRENCY, queue.length))
      .fill(null)
      .map(async () => {
        while (queue.length > 0 && !partFailure) {
          const part = queue.shift();
          if (!part) return;
          const start = (part.partNumber - 1) * partSize;
          const end = Math.min(start + partSize, input.file.size);
          const chunk = input.file.slice(start, end);
          const etag = await uploadMultipartPart({
            partNumber: part.partNumber,
            uploadUrl: part.uploadUrl,
            chunk,
          }).catch((error: unknown) => {
            partFailure = error;
            throw error;
          });
          uploadedParts.push({ partNumber: part.partNumber, etag });
          completedBytes += end - start;
          input.onProgress?.(
            Math.min(completedBytes, input.file.size),
            input.file.size,
          );
        }
      });
    await Promise.allSettled(workers);
    if (partFailure) throw partFailure;
    uploadedParts.sort((a, b) => a.partNumber - b.partNumber);

    const completed = await completeMultipartUpload({
      key: target.key,
      uploadId: target.multipart.uploadId,
      parts: uploadedParts,
    });

    if ("error" in completed) {
      throw new Error(completed.error);
    }

    return {
      assetId: target.assetId,
      key: completed.key,
      url: completed.url,
    };
  } catch (error) {
    const aborted = await abortMultipartUpload({
      key: target.key,
      uploadId: multipart.uploadId,
    });
    if ("error" in aborted) {
      logger.warn("[upload-pipeline] failed to abort multipart upload", {
        key: target.key,
        uploadId: multipart.uploadId,
        error: aborted.error,
      });
    }
    throw error;
  }
};

export type StoryMediaProgressStage =
  | "encoding"
  | "moderating"
  | "uploading"
  | "done";

export type StoryMediaProgress = {
  stage: StoryMediaProgressStage;
  fraction?: number;
};

export type MediaPipelineErrorCode = "moderation_blocked" | "video_too_long";

export class MediaPipelineError extends Error {
  readonly code: MediaPipelineErrorCode;

  constructor(code: MediaPipelineErrorCode, message: string) {
    super(message);
    this.name = "MediaPipelineError";
    this.code = code;
  }
}

const STORY_VIDEO_MAX_DURATION_MS = 60_000;

/**
 * Full single-file pipeline for the Stories composer — no UI required.
 *
 * Image: AVIF/WebP encode (worker) → moderation (hard block) → ThumbHash +
 * dimensions → upload. Video: duration probe (≤60s) → poster frame →
 * upload video + poster (poster is best-effort, like the ImageUpload grid).
 *
 * Throws `MediaPipelineError` with code "moderation_blocked" or
 * "video_too_long"; all other failures propagate as plain errors.
 */
export async function processStoryMedia(
  file: File,
  opts: {
    scope: MediaUploadScope;
    /** Groups draft uploads under one prefix; generated per call if omitted. */
    draftId?: string;
    onProgress?: (p: StoryMediaProgress) => void;
    onUploaded?: (asset: MediaAssetSlim) => void;
    signal?: AbortSignal;
    target?: RequestUploadTargetSuccess;
  },
): Promise<MediaAssetSlim> {
  const { scope, onProgress } = opts;
  const draftId = opts.draftId ?? createShortRandomId(12);
  const isVideo = file.type.startsWith("video/");

  if (!isVideo) {
    onProgress?.({ stage: "encoding" });
    const encoded = await encodeImageInWorker(file);

    onProgress?.({ stage: "moderating" });
    const moderation = await moderateImageFile(file);
    if (!moderation.ok) {
      throw new MediaPipelineError("moderation_blocked", moderation.reason);
    }

    // Capture placeholder + dimensions from the encoded pixels. The worker
    // already reports dimensions; the fallback path reports 0, so probe the
    // element in that case. ThumbHash is optional — never fail the upload.
    let width = encoded.width > 0 ? encoded.width : undefined;
    let height = encoded.height > 0 ? encoded.height : undefined;
    let blurHash: string | undefined;
    try {
      const imageElement = await loadImage(encoded.file);
      width = width ?? (imageElement.naturalWidth || undefined);
      height = height ?? (imageElement.naturalHeight || undefined);
      blurHash = encodeThumbHashFromImage(imageElement) ?? undefined;
    } catch (error) {
      logger.warn("[upload-pipeline] could not derive image placeholder", {
        fileName: file.name,
        error,
      });
    }

    onProgress?.({ stage: "uploading" });
    const uploaded = await uploadBinary({
      file: encoded.file,
      mimeType: encoded.mimeType,
      scope,
      draftId,
      signal: opts.signal,
      onUploaded: opts.onUploaded,
      onProgress: (sentBytes, totalBytes) =>
        onProgress?.({
          stage: "uploading",
          fraction:
            0.15 + (totalBytes > 0 ? 0.85 * (sentBytes / totalBytes) : 0),
        }),
    });

    onProgress?.({ stage: "done", fraction: 1 });
    if (uploaded.asset) return uploaded.asset;
    return {
      id: uploaded.assetId,
      key: uploaded.key,
      order: 0,
      width,
      height,
      blurHash,
    };
  }

  onProgress?.({ stage: "encoding" });
  const probe = await loadVideo(file);
  const durationMs = Number.isFinite(probe.duration)
    ? probe.duration * 1000
    : 0;
  if (durationMs > STORY_VIDEO_MAX_DURATION_MS) {
    throw new MediaPipelineError(
      "video_too_long",
      `Story videos must be ${STORY_VIDEO_MAX_DURATION_MS / 1000} seconds or shorter.`,
    );
  }

  onProgress?.({ stage: "uploading" });
  const uploadedVideo = await uploadBinary({
    file,
    mimeType: file.type || "video/mp4",
    scope,
    draftId,
    signal: opts.signal,
    onUploaded: opts.onUploaded,
    onProgress: (sentBytes, totalBytes) =>
      onProgress?.({
        stage: "uploading",
        fraction: totalBytes > 0 ? (0.85 * sentBytes) / totalBytes : 0,
      }),
  });
  onProgress?.({ stage: "done", fraction: 1 });
  if (uploadedVideo.asset) return uploadedVideo.asset;
  return {
    id: uploadedVideo.assetId,
    key: uploadedVideo.key,
    order: 0,
    width: probe.width > 0 ? Math.round(probe.width) : undefined,
    height: probe.height > 0 ? Math.round(probe.height) : undefined,
  };
}
